import {
  Divider,
  Typography,
  Button,
  Upload,
  message,
  Select,
  Spin,
  Switch,
  Space,
} from 'antd'
import { useState, useEffect, useRef } from 'react'
import {
  LockOutlined,
  UnlockOutlined,
  GlobalOutlined,
  PushpinFilled,
  PushpinOutlined,
} from '@ant-design/icons'

import { getBots, getUsers } from 'src/api/user'
import { savePost } from 'src/api/social'
import { fetchCountryProvider } from 'src/api/authen'
import EmojiPicker from 'emoji-picker-react'
import MentionEmojiInput from 'src/components/Input/MentionsEmojiInput'
import './Post.css'
const { Title } = Typography

const Post = () => {
  const [postContent, setPostContent] = useState('')
  const [attachments, setAttachments] = useState<any[]>([])
  const [userData, setUserData] = useState([])
  const [userId, setUserId] = useState(undefined)
  const [loading, setLoading] = useState(false)
  const newImages = useRef<File[]>([])
  const [countries, setCountries] = useState([])
  const [country, setCountry] = useState('TN')
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [fileList, setFileList] = useState<any[]>([])
  const [mentionMap, setMentionMap] = useState({})
  const [isPrivate, setIsPrivate] = useState(false)
  const [isPinned, setIsPinned] = useState(false)

  const dragItem = useRef<any>(null)

  const handleInputChange = (e) => {
    setUserId(e)
  }

  const handleDragStart = (e, file) => {
    dragItem.current = file
  }

  const handleDrop = (e, targetFile) => {
    const fromIndex = fileList.findIndex((f) => f.uid === dragItem.current.uid)
    const toIndex = fileList.findIndex((f) => f.uid === targetFile.uid)

    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return

    const updatedList = [...fileList]
    const [movedFile] = updatedList.splice(fromIndex, 1)
    updatedList.splice(toIndex, 0, movedFile)

    setFileList(updatedList)
    newImages.current = updatedList.map((f) => f.originFileObj)
  }

  const allowedFileTypes = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ]

  const fetchCountry = async () => {
    try {
      const data = await fetchCountryProvider()
      const countryOptions = data.data.map((item) => ({
        label: item.countryName,
        value: item.countryCode,
      }))
      setCountries(countryOptions)
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  const handleSubmit = async () => {
    let formattedContent = postContent
    if (postContent.trim() || attachments.length) {
      setLoading(true)

      for (const username in mentionMap) {
        const regex = new RegExp(`@${username}\\b`, 'g')
        const tag = `<mention userId="${mentionMap[username]}">@${username}</mention>`
        formattedContent = formattedContent.replace(regex, tag)
      }
      formattedContent = formattedContent.replace(
        /#([\p{L}\p{N}_]+)/gu,
        '<hashtag>#$1</hashtag>'
      )
      try {
        await savePost(
          country,
          userId,
          formattedContent,
          newImages.current,
          isPrivate,
          isPinned
        )
        message.success('Post submitted successfully!')
        setFileList([])
        newImages.current = []
        setPostContent('')
      } catch (error) {
        console.log('error', error)
        message.error(
          error.meta_data.message +
            ' Valid Format :  jpg,jpeg,png,gif,bmp,tiff,webp,mp4,avi,mkv,mov,flv,wmv,webm'
        )
      } finally {
        setLoading(false)
        setIsPrivate(false)
        setIsPinned(false)
      }
    } else {
      message.warning(
        'Please add some content or an attachment before submitting!'
      )
    }
  }

  const fetchBotUser = async () => {
    const response = await getBots()
    const usersList = response.data.map((element) => ({
      label: element.username,
      value: element.user_id,
    }))
    setUserId(usersList[0].value)
    setUserData(usersList)
  }

  const handleCountryChange = (value) => {
    setCountry(value)
  }

  const handleEmojiSelect = (emojiObject) => {
    const textarea = document.getElementById(
      'editTextArea'
    ) as HTMLTextAreaElement
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    setPostContent((prevContent) => {
      const textBefore = prevContent.slice(0, start)
      const textAfter = prevContent.slice(end)

      return textBefore + emojiObject.emoji + textAfter
    })
    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd =
        start + emojiObject.emoji.length
      textarea.focus()
    }, 0)
  }

  const handleUploadChange = ({ file, fileList: newFileList }) => {
    if (!allowedFileTypes.includes(file.type)) {
      message.error('Invalid file type')
      return
    }

    const updatedFiles = newFileList.map((file) => {
      if (!file.url && !file.preview) {
        file.preview = URL.createObjectURL(file.originFileObj)
      }
      return file
    })

    setFileList(updatedFiles)
    newImages.current = updatedFiles.map((f) => f.originFileObj)
  }

  const handleRemove = (file) => {
    const updatedFiles = fileList.filter((item) => item.uid !== file.uid)
    setFileList(updatedFiles)
    newImages.current = updatedFiles.map((f) => f.originFileObj)
  }

  useEffect(() => {
    fetchBotUser()
    fetchCountry()
  }, [])

  return (
    <Spin spinning={loading} tip="Submitting...">
      <div style={{ padding: '16px', position: 'relative' }}>
        <Title level={3}>Create a Bot Post</Title>
        <Divider />
        <Select
          placeholder="Select Country"
          value={country}
          onChange={handleCountryChange}
          options={countries}
          style={{ width: '12%' }}
        />
        <Select
          placeholder="Username"
          allowClear
          options={userData}
          onChange={handleInputChange}
          value={userId}
          showSearch
          optionFilterProp="label"
          style={{ marginLeft: 10, width: '10%' }}
        />
        <Space
          align="center"
          style={{ marginTop: 12, marginBottom: 12, marginLeft: 12 }}
        >
          <Switch
            checked={isPrivate}
            onChange={setIsPrivate}
            checkedChildren={<LockOutlined />}
            unCheckedChildren={<GlobalOutlined />}
            size="small"
          />
          {isPrivate ? (
            <>
              <LockOutlined style={{ color: '#cf1322', marginRight: 4 }} />
              <span>Private Post</span>
            </>
          ) : (
            <>
              <UnlockOutlined style={{ color: '#389e0d', marginRight: 4 }} />
              <span>Public Post</span>
            </>
          )}
        </Space>
        <Space
          align="center"
          style={{ marginTop: 12, marginBottom: 12, marginLeft: 16 }}
        >
          <Switch
            checked={isPinned}
            onChange={setIsPinned}
            size="small"
            checkedChildren={<PushpinFilled />}
            unCheckedChildren={<PushpinOutlined />}
          />
          {isPinned ? (
            <>
              <PushpinFilled style={{ color: '#fa541c', marginRight: 4 }} />
              <span>Pinned Post</span>
            </>
          ) : (
            <>
              <PushpinOutlined style={{ color: '#8c8c8c', marginRight: 4 }} />
              <span>Not Pinned</span>
            </>
          )}
        </Space>

        <div style={{ position: 'relative' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: '5px',
            }}
          ></div>
          <MentionEmojiInput
            value={postContent}
            onChange={setPostContent}
            fetchMentions={async (query) => {
              const res = await getUsers(0, 10, query, country)
              return res.data.map((user) => ({
                label: user.userName,
                value: user.userName,
                rawId: user.userId,
              }))
            }}
            placeholder="Write your post here..."
            onMentionMapChange={setMentionMap}
          />

          {showEmojiPicker && (
            <div style={{ position: 'absolute', zIndex: 2, right: 0, top: 40 }}>
              <EmojiPicker onEmojiClick={handleEmojiSelect} />
            </div>
          )}
        </div>
        <div
          style={{
            marginTop: 10,
          }}
        >
          <Upload
            beforeUpload={() => false}
            className="custom-upload"
            showUploadList={{ showRemoveIcon: true, showPreviewIcon: false }}
            fileList={fileList}
            onChange={handleUploadChange}
            onRemove={handleRemove}
            listType="picture-card"
            accept={allowedFileTypes.join(',')}
            multiple
            itemRender={(originNode, file, currFileList, actions) => (
              <div
                draggable
                onDragStart={(e) => handleDragStart(e, file)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => handleDrop(e, file)}
              >
                {originNode}
              </div>
            )}
          >
            {fileList.length < 10 && 'Attach File'}
          </Upload>
        </div>

        <Button
          type="primary"
          style={{
            width: '104px',
          }}
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </div>
    </Spin>
  )
}

export default Post
